import { Link, useLocation, Navigate } from "react-router-dom";
import { Shield, FileText, RefreshCw, ArrowUpRight, Mail } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";

const LAST_UPDATED = "August 6, 2026";
const BIZ_EMAIL = "bookings@ninacapone.com";
const BIZ_NAME = "Nina Capone Media";

// --- Content blocks (single source of truth, easy to update) ---
const TERMS_SECTIONS = [
  {
    h: "Acceptance of Terms",
    p: `By accessing ninacapone.com (the "Site"), purchasing a product, or subscribing to the Inner Circle paid membership, you agree to be bound by these Terms of Service. If you do not agree, do not use the Site. ${BIZ_NAME} may update these Terms from time to time; the "Last Updated" date above reflects the most recent revision.`,
  },
  {
    h: "Intellectual Property",
    p: `NINA CAPONE™ is the professional stage name and claimed trademark and service mark of Jamilah N. Lawry. The Nina Capone name, logos, visual identity, music, recordings, videos, photography, written content, merchandise designs, and other original materials displayed on this website are owned or lawfully controlled by Jamilah N. Lawry, her authorized business entities, or their licensors. No person or organization may use the NINA CAPONE name, branding, likeness, or related materials in a manner that falsely suggests sponsorship, affiliation, endorsement, partnership, or authorization without prior written permission. Permission to access this website does not grant a license to reproduce, distribute, modify, sell, commercially exploit, or create derivative works from any protected material.`,
  },
  {
    h: "Eligibility",
    p: `You must be at least 13 years old to use the Site and at least 18 (or the age of majority in your jurisdiction) to purchase a paid Inner Circle membership or any physical merchandise. By submitting payment, you represent that the information you provide is accurate and that you are authorized to use the payment method.`,
  },
  {
    h: "Inner Circle Membership",
    p: `The paid Inner Circle membership is billed monthly at the stated price (currently US$20/month) through Stripe. Your subscription renews automatically each month until you cancel. You can cancel any time through the Stripe Customer Portal — your access continues until the end of the current billing period. We do not pro-rate partial months. See the Refund Policy for our specific stance on refunds.`,
  },
  {
    h: "Backstage Live Access",
    p: `Backstage live sessions on Be.Live are reserved for active paid Inner Circle members. The join URL is shared on the /backstage page after we verify your membership email. The link is for personal use only — do not share, screen-share, or rebroadcast the stream. Recording the live session in part or whole is prohibited without prior written consent.`,
  },
  {
    h: "Content & Intellectual Property",
    p: `All music, audio, video, written content, photography, and brand identity on this Site are owned by ${BIZ_NAME} or its licensors. You may stream and share links to publicly posted content. You may not download, copy, redistribute, modify, sample, or commercially exploit any work without an explicit written license. Fair-use commentary and editorial citation are welcome.`,
  },
  {
    h: "User-Submitted Content",
    p: `Fan mail (text and voice notes), story submissions, and bookings inquiries are private and reviewed by ${BIZ_NAME}. We will not publicly display your message, name, or contact info without your prior consent. By submitting, you grant us a non-exclusive, perpetual license to use the content internally and, with your consent, to feature you in newsletters or social posts.`,
  },
  {
    h: "Prohibited Conduct",
    p: `Do not use the Site to: (a) harass, threaten, or harm anyone; (b) upload audio, photos, or text containing hate speech, sexually explicit material involving minors, or content that infringes third-party rights; (c) attempt to bypass our authentication or payment systems; (d) scrape, crawl, or load-test the Site without permission. We reserve the right to revoke memberships and ban accounts that violate these rules — no refund for terminated abusive accounts.`,
  },
  {
    h: "Disclaimers",
    p: `The Site and all content are provided "as is" without warranty of any kind. We do not guarantee uninterrupted access, error-free streams, or compatibility with every device. Our wellness blog posts are editorial and not medical, legal, or financial advice — consult a licensed professional for personal decisions.`,
  },
  {
    h: "Limitation of Liability",
    p: `To the maximum extent permitted by law, ${BIZ_NAME} and its agents are not liable for any indirect, incidental, special, or consequential damages arising from your use of the Site, even if advised of the possibility. Our total aggregate liability shall not exceed the amount you paid to us in the twelve months preceding the claim.`,
  },
  {
    h: "Governing Law & Disputes",
    p: `These Terms are governed by the laws of the Commonwealth of Pennsylvania, USA, without regard to conflict-of-laws principles. Any dispute will be resolved by binding arbitration in Philadelphia, PA, except that either party may bring a small-claims action in their local court. You waive any right to participate in a class action.`,
  },
  {
    h: "Contact",
    p: `Questions about these Terms? Email ${BIZ_EMAIL}. We aim to respond within 2 business days.`,
  },
];

const PRIVACY_SECTIONS = [
  {
    h: "Information We Collect",
    p: `When you subscribe to our newsletter or send fan mail, we collect your email address, optional name, and the message content (text or audio). When you purchase a membership or merchandise, Stripe collects and processes your payment details — we never see or store full card numbers. Your IP address and approximate location are logged for fraud prevention and rate limiting. Voice notes are stored as audio files in Emergent Object Storage and remain private to ${BIZ_NAME}.`,
  },
  {
    h: "How We Use It",
    p: `We use your information to (a) deliver the newsletter and event blasts you opted into, (b) fulfil membership perks (Backstage access, monthly featured-supporter drawing), (c) send transactional emails (purchase receipts, booking replies, password resets), and (d) improve the Site. We never sell your data to third parties.`,
  },
  {
    h: "Email & Double Opt-In",
    p: `All marketing emails require a double opt-in: you confirm via a link after subscribing. Every newsletter contains an unsubscribe link at the footer (CAN-SPAM compliant). One-click unsubscribe removes you from all future marketing — you'll still receive transactional emails (receipts, account notices) if you have an active subscription.`,
  },
  {
    h: "Cookies",
    p: `We use a small number of first-party cookies for: keeping you logged into the Admin Dashboard (admins only), remembering your form draft state, and providing Stripe payment widgets. In addition, third-party advertising partners (see "Advertising" below) may set cookies to serve and measure ads. You can control cookies through your browser settings and the ad-personalisation options described below.`,
  },
  {
    h: "Advertising (Google AdSense)",
    p: `This Site displays ads served by Google AdSense. Third-party vendors, including Google, use cookies to serve ads based on your prior visits to this and other websites. Google's use of advertising cookies enables it and its partners to serve ads to you based on your visit to this Site and/or other sites on the internet. You may opt out of personalised advertising by visiting Google Ads Settings (https://www.google.com/settings/ads), and you can opt out of third-party vendor cookies for personalised advertising at https://www.aboutads.info/choices. For more information on how Google uses data when you use our partners' sites or apps, see https://policies.google.com/technologies/partner-sites.`,
  },
  {
    h: "Payment Data",
    p: `Stripe is our PCI-DSS-compliant payment processor. Card numbers, CVCs, and bank credentials are submitted directly to Stripe — they do not pass through our servers. We retain only the Stripe customer ID and subscription metadata (status, current period end) to manage your membership. Stripe's privacy policy applies to data they collect.`,
  },
  {
    h: "Data Retention",
    p: `We keep newsletter subscriptions until you unsubscribe. Fan mail and voice notes are kept for the lifetime of the membership and may be archived afterward to preserve our editorial record (unless you request deletion). Payment records and invoices are retained for 7 years for tax compliance.`,
  },
  {
    h: "Your Rights (GDPR / CCPA)",
    p: `You have the right to access, correct, export, or delete your personal data. Email ${BIZ_EMAIL} with the subject "Data Request" and include the email address tied to your account. We will respond within 30 days. Note that we will need to verify your identity before fulfilling deletion requests.`,
  },
  {
    h: "Security",
    p: `We use industry-standard encryption (TLS in transit, encrypted at rest in MongoDB Atlas and Emergent Object Storage). Admin access is gated by JWT-based authentication. Backstage stream URLs are never exposed in public API responses. If we ever experience a data incident affecting your personal information, we will notify you within 72 hours.`,
  },
  {
    h: "Children",
    p: `The Site is not directed at children under 13. We do not knowingly collect personal information from anyone under 13. If you believe we have inadvertently collected such information, email ${BIZ_EMAIL} and we will delete it promptly.`,
  },
  {
    h: "Contact",
    p: `Questions about this Privacy Policy or your data? Email ${BIZ_EMAIL}.`,
  },
];

const REFUND_SECTIONS = [
  {
    h: "Inner Circle Monthly Membership",
    p: `Because our Inner Circle membership delivers immediate digital perks (Backstage live access, exclusive newsletter content, monthly featured-supporter drawing entries), monthly fees are generally non-refundable once the billing period has started. You can cancel any time through the Stripe Customer Portal — your access continues until the end of the current period, and you will not be charged again.`,
  },
  {
    h: "Cancellation",
    p: `To cancel: visit the /fan-club page → "Manage Membership" → enter your email → open the Stripe portal → select Cancel. Cancellation is effective at the end of the current billing period. No partial-month refunds.`,
  },
  {
    h: "Refund Exceptions",
    p: `We will issue a full refund for the most recent month if: (a) you were billed after cancelling and we can verify the cancel timestamp predated the charge, (b) the charge appears to be unauthorized or fraudulent, (c) we materially failed to deliver the advertised perks (e.g. you missed every scheduled Backstage session because of a documented technical failure on our end), or (d) you are requesting a refund within 7 days of your very first charge and have not used any of the paid perks (no Backstage join attempts).`,
  },
  {
    h: "Merchandise & Physical Goods",
    p: `Physical merchandise (vinyl, CDs, apparel) sold through the Shop is refundable if you contact us within 14 days of delivery. The item must be unused and in its original condition. Shipping costs are non-refundable. Defective items are replaced free of charge — email a photo of the defect to ${BIZ_EMAIL}.`,
  },
  {
    h: "Digital Downloads",
    p: `Digital downloads are non-refundable once the download link has been clicked or the file accessed. If a file fails to download or is corrupted, contact us and we'll resend or refund as appropriate.`,
  },
  {
    h: "How to Request a Refund",
    p: `Email ${BIZ_EMAIL} with the subject "Refund Request" and include: (a) the email tied to your account, (b) the Stripe receipt or order number, (c) the reason for the request. We respond within 2 business days and process approved refunds within 5–10 business days through the original payment method.`,
  },
  {
    h: "Chargebacks",
    p: `Please contact us before initiating a chargeback — we can almost always resolve issues directly and faster than your bank. Chargebacks filed without first attempting to contact us may result in your membership being cancelled without refund and your email being blocked from future signups.`,
  },
];

const PAGE_CONFIG = {
  terms: {
    title: "Terms of Service",
    icon: FileText,
    eyebrow: "Legal · Terms",
    sections: TERMS_SECTIONS,
    intro: `These Terms govern your use of the Site, your Inner Circle membership, and any merchandise you purchase from ${BIZ_NAME}.`,
  },
  privacy: {
    title: "Privacy Policy",
    icon: Shield,
    eyebrow: "Legal · Privacy",
    sections: PRIVACY_SECTIONS,
    intro: `We collect the minimum data needed to run the Site, deliver your membership perks, and stay in legal compliance. We never sell your information.`,
  },
  refund: {
    title: "Refund Policy",
    icon: RefreshCw,
    eyebrow: "Legal · Refunds",
    sections: REFUND_SECTIONS,
    intro: `Below is our honest, plain-English policy on refunds. If something feels off about your purchase, email us — we're reasonable, and we'd rather make it right than fight a chargeback.`,
  },
};

export default function LegalPage() {
  const { pathname } = useLocation();
  const kind = pathname.replace(/^\//, "").split("/")[0]; // 'terms' | 'privacy' | 'refund'
  const cfg = PAGE_CONFIG[kind];
  if (!cfg) return <Navigate to="/" replace />;
  const Icon = cfg.icon;

  return (
    <div data-testid={`legal-page-${kind}`}>
      {/* HERO */}
      <section className="relative py-24 lg:py-32 border-b border-white/5 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            background:
              "radial-gradient(ellipse 60% 60% at 0% 0%, rgba(232,180,184,0.08), transparent 60%), radial-gradient(ellipse 70% 70% at 100% 100%, rgba(74,14,62,0.25), transparent 60%)",
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12 space-y-5">
          <SectionLabel label={cfg.eyebrow} />
          <h1
            className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[0.95]"
            data-testid={`legal-title-${kind}`}
          >
            <Icon
              size={42}
              strokeWidth={1}
              style={{ color: "var(--gold)" }}
              className="inline-block mr-3 mb-2"
            />
            {cfg.title}
          </h1>
          <p className="text-white/65 leading-relaxed max-w-3xl text-base sm:text-lg">
            {cfg.intro}
          </p>
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">
            Last Updated · {LAST_UPDATED}
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-16 lg:py-20">
        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
          {/* TOC */}
          <aside
            className="lg:col-span-4 lg:sticky lg:top-24 self-start hidden lg:block"
            data-testid={`legal-toc-${kind}`}
          >
            <p className="eyebrow mb-4">In This Page</p>
            <nav className="space-y-2">
              {cfg.sections.map((s, i) => (
                <a
                  key={s.h}
                  href={`#sec-${i}`}
                  className="block text-sm text-white/55 hover:text-[color:var(--gold)] transition leading-snug"
                >
                  {i + 1}. {s.h}
                </a>
              ))}
            </nav>
          </aside>

          <article className="lg:col-span-8 space-y-10">
            {cfg.sections.map((s, i) => (
              <section key={s.h} id={`sec-${i}`} className="space-y-3 scroll-mt-24">
                <h2 className="font-serif font-light tracking-tight text-2xl lg:text-3xl leading-tight">
                  <span style={{ color: "var(--gold)" }}>{String(i + 1).padStart(2, "0")}.</span>{" "}
                  {s.h}
                </h2>
                <p className="text-white/75 leading-relaxed text-base">{s.p}</p>
              </section>
            ))}

            {/* Cross-links */}
            <div className="border-t border-white/10 pt-10 flex flex-wrap gap-3">
              <p className="eyebrow w-full mb-1">Related</p>
              {kind !== "terms" && (
                <Link
                  to="/terms"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] px-4 py-2 border border-white/15 rounded-full hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition"
                  data-testid="legal-link-terms"
                >
                  <FileText size={12} /> Terms <ArrowUpRight size={11} />
                </Link>
              )}
              {kind !== "privacy" && (
                <Link
                  to="/privacy"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] px-4 py-2 border border-white/15 rounded-full hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition"
                  data-testid="legal-link-privacy"
                >
                  <Shield size={12} /> Privacy <ArrowUpRight size={11} />
                </Link>
              )}
              {kind !== "refund" && (
                <Link
                  to="/refund"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] px-4 py-2 border border-white/15 rounded-full hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition"
                  data-testid="legal-link-refund"
                >
                  <RefreshCw size={12} /> Refunds <ArrowUpRight size={11} />
                </Link>
              )}
              <a
                href={`mailto:${BIZ_EMAIL}?subject=${encodeURIComponent(cfg.title + " — Question")}`}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] px-4 py-2 border border-[color:var(--gold)]/40 rounded-full text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 transition"
                data-testid="legal-link-email"
              >
                <Mail size={12} /> Email Us <ArrowUpRight size={11} />
              </a>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
